import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import CloseIcon from "@mui/icons-material/Close";
import BASE_URL from "../config/appconfig";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "textarea", label: "Long Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "select", label: "Single Select" },
  { value: "multi-select", label: "Multi Select" },
  { value: "checkbox", label: "Checkbox (Yes/No)" },
];

const emptyForm = {
  field_label: "",
  field_type: "text",
  options_input: "",
  is_required: false,
};

const toFieldKey = (label) =>
  label
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");

const FieldConfigManager = ({ open, handleClose, onRefresh, showSnackbar }) => {
  const [configs, setConfigs] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const fetchConfigs = () => {
    fetch(`${BASE_URL}/api/field-config/all`)
      .then((r) => r.json())
      .then((result) => {
        if (result.success) setConfigs(result.data);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (open) fetchConfigs();
  }, [open]);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const openAdd = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (cfg) => {
    setForm({
      field_label: cfg.field_label,
      field_type: cfg.field_type,
      options_input: (cfg.options || []).join(", "),
      is_required: cfg.is_required || false,
    });
    setEditingId(cfg._id);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const parseOptions = (input) =>
    input
      .split(",")
      .map((o) => o.trim())
      .filter(Boolean);

  const handleSave = async () => {
    if (!form.field_label.trim()) return;
    setLoading(true);
    try {
      const needsOptions =
        form.field_type === "select" || form.field_type === "multi-select";
      const payload = {
        field_label: form.field_label.trim(),
        field_type: form.field_type,
        options: needsOptions ? parseOptions(form.options_input) : [],
        is_required: form.is_required,
        display_order: configs.length,
      };

      if (!editingId) {
        payload.field_key = toFieldKey(form.field_label);
      }

      const url = editingId
        ? `${BASE_URL}/api/field-config/${editingId}`
        : `${BASE_URL}/api/field-config`;
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();

      if (result.success) {
        showSnackbar(editingId ? "Field updated" : "Field added", "success");
        fetchConfigs();
        onRefresh?.();
        cancelForm();
      } else {
        showSnackbar(result.message || "Failed to save", "error");
      }
    } catch {
      showSnackbar("Server error", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${BASE_URL}/api/field-config/${id}`, {
        method: "DELETE",
      });
      const result = await res.json();
      if (result.success) {
        showSnackbar("Field deleted", "success");
        fetchConfigs();
        onRefresh?.();
      } else {
        showSnackbar(result.message || "Failed to delete", "error");
      }
    } catch {
      showSnackbar("Server error", "error");
    }
  };

  const handleToggleActive = async (cfg) => {
    try {
      await fetch(`${BASE_URL}/api/field-config/${cfg._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          field_label: cfg.field_label,
          field_type: cfg.field_type,
          options: cfg.options,
          is_required: cfg.is_required,
          is_active: !cfg.is_active,
          display_order: cfg.display_order,
        }),
      });
      fetchConfigs();
      onRefresh?.();
    } catch {
      showSnackbar("Server error", "error");
    }
  };

  const needsOptions =
    form.field_type === "select" || form.field_type === "multi-select";

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: "editor-dialog-paper" }}
    >
      <DialogContent className="editor-dialog-content">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            className="editor-dialog-title"
            sx={{ mb: "0 !important" }}
          >
            Custom Field Manager
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 2 }}>
          Define additional fields (e.g. Trainings Attended) that appear in the
          Add Contact form. Email is always required and cannot be configured
          here.
        </Typography>

        {/* Existing fields list */}
        {configs.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {configs.map((cfg) => (
              <Box
                key={cfg._id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 1,
                  px: 1.5,
                  mb: 0.5,
                  borderRadius: 1,
                  border: "1px solid #e5e7eb",
                  bgcolor: cfg.is_active ? "#fff" : "#f9fafb",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: cfg.is_active ? "#1f2937" : "#9ca3af",
                    }}
                  >
                    {cfg.field_label}
                    {cfg.is_required && (
                      <span style={{ color: "#ef4444", marginLeft: 3 }}>*</span>
                    )}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      mt: 0.3,
                      flexWrap: "wrap",
                    }}
                  >
                    <Chip
                      label={
                        FIELD_TYPES.find((t) => t.value === cfg.field_type)
                          ?.label || cfg.field_type
                      }
                      size="small"
                      sx={{ fontSize: 10, height: 18 }}
                    />
                    {(cfg.options || []).slice(0, 3).map((opt) => (
                      <Chip
                        key={opt}
                        label={opt}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 10, height: 18 }}
                      />
                    ))}
                    {(cfg.options || []).length > 3 && (
                      <Typography
                        sx={{
                          fontSize: 10,
                          color: "#9ca3af",
                          alignSelf: "center",
                        }}
                      >
                        +{cfg.options.length - 3} more
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                  <Switch
                    size="small"
                    checked={!!cfg.is_active}
                    onChange={() => handleToggleActive(cfg)}
                  />
                  <IconButton
                    size="small"
                    onClick={() => openEdit(cfg)}
                    sx={{ p: "3px" }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 20, color: "#6b7280" }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteConfirmId(cfg._id)}
                    sx={{ p: "3px" }}
                  >
                    <DeleteOutlinedIcon sx={{ fontSize: 20, color: "#ef4444" }} />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {configs.length === 0 && !showForm && (
          <Typography
            sx={{ fontSize: 12, color: "#9ca3af", textAlign: "center", py: 3 }}
          >
            No custom fields yet. Click "Add Field" to create one.
          </Typography>
        )}

        {/* Add/Edit form */}
        {showForm && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography
              sx={{ fontSize: 13, fontWeight: 700, color: "#374151", mb: 1.5 }}
            >
              {editingId ? "Edit Field" : "New Field"}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                name="field_label"
                label="Field Label"
                size="small"
                fullWidth
                value={form.field_label}
                onChange={handleFormChange}
                placeholder="e.g. Trainings Attended"
                helperText={
                  !editingId && form.field_label
                    ? `Key: ${toFieldKey(form.field_label)}`
                    : ""
                }
              />
              <TextField
                select
                name="field_type"
                label="Field Type"
                size="small"
                fullWidth
                value={form.field_type}
                onChange={handleFormChange}
              >
                {FIELD_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
              {needsOptions && (
                <TextField
                  name="options_input"
                  label="Options (comma-separated)"
                  size="small"
                  fullWidth
                  value={form.options_input}
                  onChange={handleFormChange}
                  placeholder="Option 1, Option 2, Option 3"
                  helperText="Separate each option with a comma"
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    name="is_required"
                    size="small"
                    checked={form.is_required}
                    onChange={handleFormChange}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13 }}>Required field</Typography>
                }
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 2,
              }}
            >
              <Button size="small" onClick={cancelForm} color="inherit">
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={!form.field_label.trim() || loading}
                onClick={handleSave}
                sx={{
                  backgroundColor: "#ef2d2d",
                  "&:hover": { backgroundColor: "#d91c1c" },
                }}
              >
                {loading ? "Saving..." : editingId ? "Update" : "Add Field"}
              </Button>
            </Box>
          </>
        )}

        {!showForm && (
          <Button
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
            onClick={openAdd}
            sx={{ fontSize: 12, fontWeight: 700, color: "#374151", mt: 0.5 }}
          >
            Add Field
          </Button>
        )}
      </DialogContent>
    </Dialog>

    {/* Delete confirmation dialog */}
    <Dialog
      open={Boolean(deleteConfirmId)}
      onClose={() => setDeleteConfirmId(null)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>
        Delete Custom Field?
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: "#6b7280" }}>
          This will permanently delete the field and all its data. This action
          cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          size="small"
          onClick={() => setDeleteConfirmId(null)}
          color="inherit"
          sx={{ fontSize: 13 }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            handleDelete(deleteConfirmId);
            setDeleteConfirmId(null);
          }}
          sx={{
            fontSize: 13,
            backgroundColor: "#ef2d2d",
            "&:hover": { backgroundColor: "#d91c1c" },
          }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default FieldConfigManager;
